import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  employmentRoleLabel,
  employmentRoleOptionsForBusinessUnit,
} from "../utils/roleLabels";
import "./PlatformBusinessUnits.css";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function cleanText(v) {
  return safeString(v).trim().replace(/\s+/g, " ");
}

function normalizeStoreCode(v) {
  return cleanText(v).toUpperCase().replace(/[^A-Z0-9_-]/g, "");
}

function normalizePlatformRole(v) {
  const r = safeString(v).trim().toUpperCase();
  if (r === "SUPER_ADMIN" || r === "ADMIN" || r === "SUPPORT") return r;
  return "";
}

function normalizeEmploymentRole(v) {
  const r = cleanText(v).toLowerCase().replace(/\s+/g, "_");
  const allowed = new Set([
    "manager",
    "dispatcher",
    "advisor",
    "tech",
    "foreman",
    "parts",
    "staff",
    "admin",
    "support",
    "super_admin",
  ]);
  return allowed.has(r) ? r : "manager";
}

function formatEmploymentRoleValue(v) {
  return normalizeEmploymentRole(v || "manager");
}

function normalizeBU(row) {
  const r = row && typeof row === "object" ? row : {};
  return {
    id: String(r?.id ?? r?.business_unit_id ?? ""),
    groupName: cleanText(r?.group_name ?? r?.groupName ?? ""),
    storeName: cleanText(r?.store_name ?? r?.storeName ?? ""),
    storeCode: normalizeStoreCode(r?.store_code ?? r?.storeCode ?? ""),
    timezone:
      cleanText(r?.timezone ?? "America/Los_Angeles") ||
      "America/Los_Angeles",
    status: cleanText(r?.status ?? "ACTIVE").toUpperCase() || "ACTIVE",
    city: cleanText(r?.city ?? ""),
    state: cleanText(r?.state ?? "").toUpperCase(),
    phone: cleanText(r?.phone ?? ""),
  };
}

function normalizeEmployment(row) {
  const r = row && typeof row === "object" ? row : {};
  return {
    id: String(r?.id ?? ""),
    appUserId: String(r?.appUserId ?? r?.app_user_id ?? ""),
    businessUnitId: String(r?.businessUnitId ?? r?.business_unit_id ?? ""),
    appUserName: cleanText(r?.appUserName ?? r?.app_user_name),
    appUserEmail: cleanText(r?.appUserEmail ?? r?.app_user_email),
    appUserStatus: cleanText(
      r?.appUserStatus ?? r?.app_user_status ?? "ACTIVE"
    ).toUpperCase(),
    role: cleanText(r?.role),
    isActive:
      r?.isActive != null
        ? !!r.isActive
        : r?.is_active != null
        ? !!r.is_active
        : true,
    createdAt: r?.createdAt ?? r?.created_at ?? null,
  };
}

function normalizeCandidate(row) {
  const r = row && typeof row === "object" ? row : {};
  return {
    id: String(r?.id ?? ""),
    email: cleanText(r?.email).toLowerCase(),
    name: cleanText(r?.name),
    status: cleanText(r?.status ?? "ACTIVE").toUpperCase(),
    createdAt: r?.createdAt ?? r?.created_at ?? null,
  };
}

function fmtDateTime(v) {
  const s = safeString(v).trim();
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString();
}

function statusTone(status) {
  const s = safeString(status).toUpperCase();
  if (s === "ACTIVE") return "success";
  if (s === "INACTIVE") return "danger";
  return "default";
}

function formatStatus(status) {
  return safeString(status).replace(/_/g, " ");
}

function Pill({ children, tone = "default" }) {
  return (
    <span
      className={`ftbu-pill ${tone !== "default" ? `ftbu-pill--${tone}` : ""}`}
    >
      {children}
    </span>
  );
}

export default function PlatformBusinessUnits() {
  const nav = useNavigate();
  const loc = useLocation();
  const auth = useAuth();

  const token = safeString(auth?.token).trim();
  const isAuthed = !!token;

  const inPlatformRoute = loc.pathname.startsWith("/platform");
  const platformRole = normalizePlatformRole(
    auth?.user?.platformRole ?? auth?.platformRole ?? ""
  );
  const hasPlatformAccess = !!platformRole;

  const apiGet = auth?.apiGet;
  const apiPost = auth?.apiPost;
  const apiPatch = auth?.apiPatch;
  const apiDelete = auth?.apiDelete;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState("");

  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [staffLoading, setStaffLoading] = useState(false);
  const [staffRows, setStaffRows] = useState([]);

  const [showAddEmployment, setShowAddEmployment] = useState(false);
  const [employmentSaving, setEmploymentSaving] = useState(false);
  const [employmentCandidates, setEmploymentCandidates] = useState([]);
  const [employmentCandidatesLoading, setEmploymentCandidatesLoading] =
    useState(false);
  const [employmentAppUserId, setEmploymentAppUserId] = useState("");
  const [employmentRole, setEmploymentRole] = useState("manager");
  const [employmentIsActive, setEmploymentIsActive] = useState(true);

  const [form, setForm] = useState({
    groupName: "",
    storeName: "",
    storeCode: "",
    timezone: "America/Los_Angeles",
    status: "ACTIVE",
    city: "",
    state: "",
    phone: "",
  });

  const selected = useMemo(
    () => rows.find((r) => String(r.id) === String(selectedId)) || null,
    [rows, selectedId]
  );

  const [editGroupName, setEditGroupName] = useState("");
  const [editStoreName, setEditStoreName] = useState("");
  const [editStoreCode, setEditStoreCode] = useState("");
  const [editTimezone, setEditTimezone] = useState("");
  const [editStatus, setEditStatus] = useState("ACTIVE");
  const [editCity, setEditCity] = useState("");
  const [editState, setEditState] = useState("");
  const [editPhone, setEditPhone] = useState("");

  useEffect(() => {
    if (!selected) return;
    setEditMode(false);
    setEditGroupName(selected.groupName || "");
    setEditStoreName(selected.storeName || "");
    setEditStoreCode(selected.storeCode || "");
    setEditTimezone(selected.timezone || "America/Los_Angeles");
    setEditStatus(selected.status || "ACTIVE");
    setEditCity(selected.city || "");
    setEditState(selected.state || "");
    setEditPhone(selected.phone || "");
  }, [selected]);

  const sortBusinessUnits = useCallback((list) => {
    const next = [...list];
    next.sort((a, b) => {
      const aa = safeString(a.storeCode).toUpperCase() === "FLOWTRAK" ? -1 : 0;
      const bb = safeString(b.storeCode).toUpperCase() === "FLOWTRAK" ? -1 : 0;
      if (aa !== bb) return aa - bb;
      return `${a.groupName} ${a.storeName} ${a.storeCode}`.localeCompare(
        `${b.groupName} ${b.storeName} ${b.storeCode}`
      );
    });
    return next;
  }, []);

  const refresh = useCallback(async () => {
    setErr("");
    setLoading(true);
    try {
      if (!isAuthed) throw new Error("Not signed in.");
      if (!hasPlatformAccess) throw new Error("No Platform access.");
      if (!inPlatformRoute) throw new Error("Platform route required.");
      if (typeof apiGet !== "function") throw new Error("apiGet missing.");

      const data = await apiGet("/api/platform/business-units");
      const raw = Array.isArray(data?.businessUnits)
        ? data.businessUnits
        : Array.isArray(data)
        ? data
        : [];

      const list = sortBusinessUnits(raw.map(normalizeBU));
      setRows(list);

      setSelectedId((prev) => {
        if (!prev) return "";
        return list.some((x) => String(x.id) === String(prev)) ? prev : "";
      });
    } catch (e) {
      setErr(e?.message || "Failed to load business units");
      setRows([]);
      setSelectedId("");
    } finally {
      setLoading(false);
    }
  }, [apiGet, hasPlatformAccess, inPlatformRoute, isAuthed, sortBusinessUnits]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    let alive = true;

    async function loadStaff() {
      if (!selected?.id || typeof apiGet !== "function") {
        setStaffRows([]);
        return;
      }

      try {
        setStaffLoading(true);
        const data = await apiGet(
          `/api/platform/employments?businessUnitId=${encodeURIComponent(
            selected.id
          )}`
        );
        if (!alive) return;

        const raw = Array.isArray(data?.employments)
          ? data.employments
          : Array.isArray(data)
          ? data
          : [];

        setStaffRows(raw.map(normalizeEmployment));
      } catch {
        if (!alive) return;
        setStaffRows([]);
      } finally {
        if (alive) setStaffLoading(false);
      }
    }

    loadStaff();
    return () => {
      alive = false;
    };
  }, [apiGet, selected?.id]);

  useEffect(() => {
    let alive = true;

    async function loadEmploymentCandidates() {
      if (!showAddEmployment || !selected?.id || typeof apiGet !== "function") {
        return;
      }

      try {
        setEmploymentCandidatesLoading(true);
        const data = await apiGet(
          `/api/platform/employments/candidates?businessUnitId=${encodeURIComponent(
            selected.id
          )}`
        );
        if (!alive) return;

        const raw = Array.isArray(data?.candidates)
          ? data.candidates
          : Array.isArray(data)
          ? data
          : [];

        const list = raw.map(normalizeCandidate);
        setEmploymentCandidates(list);

        if (list.length) {
          setEmploymentAppUserId((prev) => {
            if (prev && list.some((x) => String(x.id) === String(prev))) {
              return prev;
            }
            return list[0].id;
          });
        } else {
          setEmploymentAppUserId("");
        }
      } catch (e) {
        if (!alive) return;
        setEmploymentCandidates([]);
        setEmploymentAppUserId("");
        setErr(e?.message || "Failed to load employment candidates");
      } finally {
        if (alive) setEmploymentCandidatesLoading(false);
      }
    }

    loadEmploymentCandidates();
    return () => {
      alive = false;
    };
  }, [apiGet, selected?.id, showAddEmployment]);

  const filtered = useMemo(() => {
    const needle = safeString(q).trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay =
        `${r.groupName} ${r.storeName} ${r.storeCode} ${r.city} ${r.state} ${r.status}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q]);

  function openNew() {
    setForm({
      groupName: "",
      storeName: "",
      storeCode: "",
      timezone: "America/Los_Angeles",
      status: "ACTIVE",
      city: "",
      state: "",
      phone: "",
    });
    setShowNew(true);
  }

  function openAddEmployment() {
    if (!selected?.id) return;
    setEmploymentAppUserId("");
    setEmploymentRole("manager");
    setEmploymentIsActive(true);
    setEmploymentCandidates([]);
    setShowAddEmployment(true);
  }

  function toggleSelected(id) {
    setSelectedId((prev) => (String(prev) === String(id) ? "" : String(id)));
  }

  async function createBU() {
    try {
      setErr("");
      if (typeof apiPost !== "function") throw new Error("apiPost missing.");

      const payload = {
        groupName: cleanText(form.groupName),
        storeName: cleanText(form.storeName),
        storeCode: normalizeStoreCode(form.storeCode),
        timezone: cleanText(form.timezone) || "America/Los_Angeles",
        status: cleanText(form.status).toUpperCase() || "ACTIVE",
        city: cleanText(form.city),
        state: cleanText(form.state).toUpperCase(),
        phone: cleanText(form.phone),
      };

      if (!payload.groupName) throw new Error("Group is required.");
      if (!payload.storeName) throw new Error("Store Name is required.");
      if (!payload.storeCode) throw new Error("Store Code is required.");

      setSaving(true);
      const res = await apiPost("/api/platform/business-units", payload);
      const created = normalizeBU(res?.businessUnit ?? res?.data ?? res);

      if (created?.id) {
        setRows((prev) => sortBusinessUnits([created, ...prev]));
        setSelectedId(created.id);
      } else {
        await refresh();
      }

      setShowNew(false);
      setForm((p) => ({ ...p, storeCode: payload.storeCode }));
    } catch (e) {
      setErr(e?.message || "Failed to create business unit");
    } finally {
      setSaving(false);
    }
  }

  async function saveDetails() {
    try {
      setErr("");
      if (!selected?.id) throw new Error("Select a Business Unit first.");
      if (typeof apiPatch !== "function") throw new Error("apiPatch missing.");

      const payload = {
        groupName: cleanText(editGroupName),
        storeName: cleanText(editStoreName),
        storeCode: normalizeStoreCode(editStoreCode),
        timezone: cleanText(editTimezone) || "America/Los_Angeles",
        status: cleanText(editStatus).toUpperCase() || "ACTIVE",
        city: cleanText(editCity),
        state: cleanText(editState).toUpperCase(),
        phone: cleanText(editPhone),
      };

      if (!payload.groupName) throw new Error("Group is required.");
      if (!payload.storeName) throw new Error("Store Name is required.");
      if (!payload.storeCode) throw new Error("Store Code is required.");

      setSaving(true);

      const res = await apiPatch(
        `/api/platform/business-units/${selected.id}`,
        payload
      );
      const updated = normalizeBU(res?.businessUnit ?? res?.data ?? res);

      setRows((prev) =>
        sortBusinessUnits(
          prev.map((x) =>
            String(x.id) === String(selected.id) ? updated : x
          )
        )
      );

      setEditStoreCode(updated.storeCode || payload.storeCode);
      setEditMode(false);
    } catch (e) {
      setErr(e?.message || "Failed to update business unit");
    } finally {
      setSaving(false);
    }
  }

  async function createEmploymentFromBusinessUnit() {
    try {
      setErr("");
      if (!selected?.id) throw new Error("Select a Business Unit first.");
      if (!employmentAppUserId) throw new Error("Select a user.");
      if (typeof apiPost !== "function") throw new Error("apiPost missing.");

      setEmploymentSaving(true);

      await apiPost("/api/platform/employments", {
        appUserId: employmentAppUserId,
        businessUnitId: selected.id,
        role: formatEmploymentRoleValue(employmentRole),
        isActive: !!employmentIsActive,
      });

      const data = await apiGet(
        `/api/platform/employments?businessUnitId=${encodeURIComponent(
          selected.id
        )}`
      );
      const raw = Array.isArray(data?.employments)
        ? data.employments
        : Array.isArray(data)
        ? data
        : [];

      setStaffRows(raw.map(normalizeEmployment));
      setShowAddEmployment(false);
      setEmploymentCandidates([]);
      setEmploymentAppUserId("");
      setEmploymentRole("manager");
      setEmploymentIsActive(true);
    } catch (e) {
      setErr(e?.message || "Failed to add employment");
    } finally {
      setEmploymentSaving(false);
    }
  }

  async function deactivateSelected() {
    try {
      setErr("");
      if (!selected?.id) throw new Error("Select a Business Unit first.");
      if (typeof apiDelete !== "function") throw new Error("apiDelete missing.");

      const name = `${selected.groupName || "—"} / ${selected.storeName || "—"}${
        selected.storeCode ? ` (${selected.storeCode})` : ""
      }`;

      const ok = window.confirm(
        `Deactivate this Business Unit?\n\n${name}\n\nThis keeps the store history and staff records intact, but prevents active store access. This is the safer customer-closeout action for production.`
      );
      if (!ok) return;

      setSaving(true);
      const res = await apiDelete(`/api/platform/business-units/${selected.id}`);
      const updated = normalizeBU(res?.businessUnit || { ...selected, status: "INACTIVE" });

      setRows((prev) =>
        prev.map((x) => (String(x.id) === String(selected.id) ? updated : x))
      );
      setSelectedId(updated.id || selected.id);
      setEditMode(false);
    } catch (e) {
      setErr(e?.message || "Failed to deactivate business unit");
    } finally {
      setSaving(false);
    }
  }

  function cancelEdit() {
    if (!selected) return;
    setEditMode(false);
    setEditGroupName(selected.groupName || "");
    setEditStoreName(selected.storeName || "");
    setEditStoreCode(selected.storeCode || "");
    setEditTimezone(selected.timezone || "America/Los_Angeles");
    setEditStatus(selected.status || "ACTIVE");
    setEditCity(selected.city || "");
    setEditState(selected.state || "");
    setEditPhone(selected.phone || "");
  }

  if (!isAuthed) {
    return (
      <div className="ft-page">
        <div className="ft-pageHeader">
          <div>
            <h1 className="ft-h1">Business Units</h1>
            <div className="ft-subtle">Please log in to continue.</div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasPlatformAccess) {
    return (
      <div className="ft-page">
        <div className="ft-pageHeader">
          <div>
            <h1 className="ft-h1">Business Units</h1>
            <div className="ft-subtle">
              Your account does not have Platform access.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!inPlatformRoute) {
    return (
      <div className="ft-page">
        <div className="ft-pageHeader">
          <div>
            <h1 className="ft-h1">Business Units</h1>
            <div className="ft-subtle">
              This page must be accessed under <b>/platform</b>.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isFlowtrakSelected =
    safeString(selected?.storeCode).toUpperCase() === "FLOWTRAK";

  return (
    <div className="ft-page ftbu-page">
      <div className="ftbu-shell">
        <div className="ftbu-pageTop">
          <div className="ftbu-titleWrap">
            <div className="ftbu-eyebrow">Platform</div>
            <h1 className="ft-title">Business Units</h1>
            <p className="ft-subtitle">
              Manage dealer groups and locations, then attach employments and
              user access.
            </p>
          </div>

          <div className="ftbu-pageActions">
            <button
              className="ft-btn ft-btn--primary"
              type="button"
              onClick={openNew}
            >
              + Add Business Unit
            </button>
            <button
              className="ft-btn ft-btn--ghost"
              type="button"
              onClick={refresh}
            >
              Refresh
            </button>
          </div>
        </div>

        {err ? (
          <div className="ft-card ft-card--error">
            <div className="ft-errorText">{err}</div>
          </div>
        ) : null}

        <div className="ftbu-workspace">
          <div className="ft-card ftbu-searchCard">
            <input
              className="ft-input ftbu-searchInput"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search group, store, code, city, state, status…"
            />
          </div>

          <div className="ftbu-layout">
            <div className="ftbu-mainColumn">
              <div className="ft-card ftbu-directoryCard">
                <div className="ft-card__header">
                  <div className="ft-card__headerTitle">Directory</div>
                  <div className="ft-card__headerSub">
                    {loading ? "Loading…" : `${filtered.length} shown`}
                  </div>
                </div>

                <div className="ft-card__body">
                  {loading ? (
                    <div className="ft-muted">Loading business units…</div>
                  ) : filtered.length === 0 ? (
                    <div className="ft-muted">
                      No Business Units yet. Click “Add Business Unit”.
                    </div>
                  ) : (
                    <div className="ftbu-list">
                      {filtered.map((r) => {
                        const active = String(r.id) === String(selectedId);
                        const isFlowtrak =
                          safeString(r.storeCode).toUpperCase() === "FLOWTRAK";

                        return (
                          <div
                            key={r.id || `${r.groupName}-${r.storeCode}`}
                            className="ftbu-rowWrap"
                          >
                            <button
                              type="button"
                              className={`ftbu-row ${active ? "is-active" : ""}`}
                              onClick={() => toggleSelected(r.id)}
                              aria-expanded={active}
                            >
                              <div className="ftbu-rowMain">
                                <div className="ftbu-rowIdentity">
                                  <div className="ftbu-nameLine">
                                    <span className="ftbu-name">
                                      {r.groupName || "—"} / {r.storeName || "—"}
                                    </span>
                                    {isFlowtrak ? (
                                      <span className="ftbu-you">platform</span>
                                    ) : null}
                                  </div>

                                  <div className="ftbu-subline">
                                    {r.city || "—"}
                                    {r.state ? `, ${r.state}` : ""}
                                  </div>
                                </div>

                                <div className="ftbu-rowPills">
                                  <Pill>{r.storeCode || "—"}</Pill>
                                  <Pill tone="muted">{r.timezone || "—"}</Pill>
                                  <Pill tone={statusTone(r.status)}>
                                    {formatStatus(r.status)}
                                  </Pill>
                                </div>
                              </div>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="ftbu-sideColumn">
              {selected ? (
                <div className="ft-card ftbu-profileCard">
                  <div className="ft-card__header">
                    <div className="ft-card__headerTitle">Profile</div>

                    <div className="ftbu-headerRight">
                      <div className="ft-card__headerSub">Selected</div>
                      <button
                        className="ft-btn ft-btn--ghost"
                        type="button"
                        onClick={() => setSelectedId("")}
                        disabled={saving}
                      >
                        Close
                      </button>
                    </div>
                  </div>

                  <div className="ft-card__body">
                    <div className="ftbu-profileHero">
                      <div className="ftbu-profileHeroText">
                        <div className="ftbu-profileName">
                          {selected.groupName || "—"} / {selected.storeName || "—"}
                        </div>
                        <div className="ftbu-profileSub">
                          {selected.city || "—"}
                          {selected.state ? `, ${selected.state}` : ""}
                        </div>
                      </div>

                      <div className="ftbu-profilePills">
                        <Pill>{selected.storeCode || "—"}</Pill>
                        <Pill tone="muted">{selected.timezone || "—"}</Pill>
                        <Pill tone={statusTone(selected.status)}>
                          {formatStatus(selected.status)}
                        </Pill>
                      </div>
                    </div>

                    <div className="ftbu-section">
                      <div className="ftbu-sectionTitle">
                        Business Unit Details
                      </div>

                      <div className="ftbu-formGrid">
                        <div>
                          <div className="ft-label">Group</div>
                          {editMode ? (
                            <input
                              className="ft-input"
                              value={editGroupName}
                              onChange={(e) => setEditGroupName(e.target.value)}
                              disabled={saving}
                            />
                          ) : (
                            <div className="ftbu-readonly">
                              {selected.groupName || "—"}
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="ft-label">Store Name</div>
                          {editMode ? (
                            <input
                              className="ft-input"
                              value={editStoreName}
                              onChange={(e) => setEditStoreName(e.target.value)}
                              disabled={saving}
                            />
                          ) : (
                            <div className="ftbu-readonly">
                              {selected.storeName || "—"}
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="ft-label">Store Code</div>
                          {editMode ? (
                            <input
                              className="ft-input"
                              value={editStoreCode}
                              onChange={(e) =>
                                setEditStoreCode(normalizeStoreCode(e.target.value))
                              }
                              disabled={saving || isFlowtrakSelected}
                            />
                          ) : (
                            <div className="ftbu-readonly">
                              {selected.storeCode || "—"}
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="ft-label">Timezone</div>
                          {editMode ? (
                            <input
                              className="ft-input"
                              value={editTimezone}
                              onChange={(e) => setEditTimezone(e.target.value)}
                              disabled={saving}
                            />
                          ) : (
                            <div className="ftbu-readonly">
                              {selected.timezone || "—"}
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="ft-label">Status</div>
                          {editMode ? (
                            <select
                              className="ft-select"
                              value={editStatus}
                              onChange={(e) => setEditStatus(e.target.value)}
                              disabled={saving}
                            >
                              <option value="ACTIVE">ACTIVE</option>
                              <option value="INACTIVE">INACTIVE</option>
                            </select>
                          ) : (
                            <div className="ftbu-readonly">
                              {formatStatus(selected.status)}
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="ft-label">Phone</div>
                          {editMode ? (
                            <input
                              className="ft-input"
                              value={editPhone}
                              onChange={(e) => setEditPhone(e.target.value)}
                              disabled={saving}
                            />
                          ) : (
                            <div className="ftbu-readonly">
                              {selected.phone || "—"}
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="ft-label">City</div>
                          {editMode ? (
                            <input
                              className="ft-input"
                              value={editCity}
                              onChange={(e) => setEditCity(e.target.value)}
                              disabled={saving}
                            />
                          ) : (
                            <div className="ftbu-readonly">
                              {selected.city || "—"}
                            </div>
                          )}
                        </div>

                        <div>
                          <div className="ft-label">State</div>
                          {editMode ? (
                            <input
                              className="ft-input"
                              value={editState}
                              onChange={(e) =>
                                setEditState(cleanText(e.target.value).toUpperCase())
                              }
                              disabled={saving}
                            />
                          ) : (
                            <div className="ftbu-readonly">
                              {selected.state || "—"}
                            </div>
                          )}
                        </div>
                      </div>

                      {isFlowtrakSelected ? (
                        <div className="ftbu-hint">
                          FLOWTRAK is reserved for your internal Platform
                          Business Unit.
                        </div>
                      ) : null}
                    </div>

                    <div className="ftbu-section">
                      <div className="ftbu-sectionHead">
                        <div className="ftbu-sectionTitle ftbu-sectionTitle--noMargin">
                          Staff / Employments
                        </div>

                        <button
                          className="ft-btn ft-btn--ghost"
                          type="button"
                          onClick={openAddEmployment}
                          disabled={!selected?.id}
                        >
                          + Add Employment
                        </button>
                      </div>

                      {staffLoading ? (
                        <div className="ft-muted">Loading staff…</div>
                      ) : staffRows.length === 0 ? (
                        <div className="ft-muted">
                          No employments found for this Business Unit yet.
                        </div>
                      ) : (
                        <div className="ftbu-staffList">
                          {staffRows.map((emp) => (
                            <div
                              key={
                                emp.id || `${emp.appUserId}-${emp.businessUnitId}`
                              }
                              className="ftbu-staffCard"
                            >
                              <div className="ftbu-staffTop">
                                <div className="ftbu-staffIdentity">
                                  <div className="ftbu-staffName">
                                    {emp.appUserName || "—"}
                                  </div>
                                  <div className="ftbu-staffEmail">
                                    {emp.appUserEmail || "—"}
                                  </div>
                                </div>

                                <div className="ftbu-staffPills">
                                  <Pill>
                                    {emp.role ? employmentRoleLabel(emp.role, selected) : "—"}
                                  </Pill>
                                  <Pill tone={emp.isActive ? "success" : "danger"}>
                                    {emp.isActive ? "Active" : "Inactive"}
                                  </Pill>
                                </div>
                              </div>

                              <div className="ftbu-staffMeta">
                                <span>{`App User: ${emp.appUserStatus || "—"}`}</span>
                                <span className="ftbu-dot" />
                                <span>
                                  {emp.createdAt
                                    ? `Added: ${fmtDateTime(emp.createdAt)}`
                                    : "Added: —"}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="ftbu-section">
                      <div className="ftbu-sectionTitle">Actions</div>
                      <div className="ftbu-actionsRow">
                        <button
                          className="ft-btn ft-btn--ghost"
                          type="button"
                          onClick={() => nav("/platform/users")}
                        >
                          View Users
                        </button>
                        {!isFlowtrakSelected ? (
                          <button
                            className="ft-btn ft-btn--ghost"
                            type="button"
                            onClick={deactivateSelected}
                            disabled={saving}
                          >
                            Deactivate Business Unit
                          </button>
                        ) : null}
                      </div>
                    </div>

                    <div className="ftbu-footerBar">
                      {!editMode ? (
                        <button
                          className="ft-btn ft-btn--primary"
                          type="button"
                          onClick={() => setEditMode(true)}
                        >
                          Edit
                        </button>
                      ) : (
                        <>
                          <button
                            className="ft-btn ft-btn--primary"
                            type="button"
                            onClick={saveDetails}
                            disabled={saving}
                          >
                            {saving ? "Saving…" : "Save Changes"}
                          </button>
                          <button
                            className="ft-btn ft-btn--ghost"
                            type="button"
                            onClick={cancelEdit}
                            disabled={saving}
                          >
                            Cancel
                          </button>
                        </>
                      )}

                      <button
                        className="ft-btn ft-btn--ghost"
                        type="button"
                        onClick={() => nav("/platform/admin")}
                        disabled={saving}
                      >
                        Back to Platform
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="ft-card ftbu-emptyCard">
                  Select a business unit to view details
                </div>
              )}
            </div>
          </div>
        </div>

        {showNew ? (
          <div className="ft-modalOverlay" role="dialog" aria-modal="true">
            <div className="ft-modal ftbu-modal">
              <div className="ft-modalHeader">
                <div>
                  <div className="ft-cardTitle">Create Business Unit</div>
                  <div className="ft-caption">Group + Store Name + Store Code</div>
                </div>

                <button
                  className="ft-btn ft-btn--ghost"
                  type="button"
                  onClick={() => (!saving ? setShowNew(false) : null)}
                >
                  Close
                </button>
              </div>

              <div className="ft-modalBody ftbu-modalBody">
                <div className="ftbu-modalGrid">
                  <div className="ftbu-modalField">
                    <div className="ft-label">Group</div>
                    <input
                      className="ft-input"
                      value={form.groupName}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, groupName: e.target.value }))
                      }
                      disabled={saving}
                    />
                  </div>

                  <div className="ftbu-modalField">
                    <div className="ft-label">Store Name</div>
                    <input
                      className="ft-input"
                      value={form.storeName}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, storeName: e.target.value }))
                      }
                      disabled={saving}
                    />
                  </div>

                  <div className="ftbu-modalField">
                    <div className="ft-label">Store Code</div>
                    <input
                      className="ft-input"
                      value={form.storeCode}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          storeCode: normalizeStoreCode(e.target.value),
                        }))
                      }
                      disabled={saving}
                    />
                  </div>

                  <div className="ftbu-modalField">
                    <div className="ft-label">Timezone</div>
                    <input
                      className="ft-input"
                      value={form.timezone}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, timezone: e.target.value }))
                      }
                      disabled={saving}
                    />
                  </div>

                  <div className="ftbu-modalField">
                    <div className="ft-label">City</div>
                    <input
                      className="ft-input"
                      value={form.city}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, city: e.target.value }))
                      }
                      disabled={saving}
                    />
                  </div>

                  <div className="ftbu-modalField">
                    <div className="ft-label">State</div>
                    <input
                      className="ft-input"
                      value={form.state}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          state: cleanText(e.target.value).toUpperCase(),
                        }))
                      }
                      disabled={saving}
                    />
                  </div>

                  <div className="ftbu-modalField">
                    <div className="ft-label">Phone</div>
                    <input
                      className="ft-input"
                      value={form.phone}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, phone: e.target.value }))
                      }
                      disabled={saving}
                    />
                  </div>

                  <div className="ftbu-modalField">
                    <div className="ft-label">Status</div>
                    <select
                      className="ft-select"
                      value={form.status}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, status: e.target.value }))
                      }
                      disabled={saving}
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="ft-modalFooter">
                <button
                  className="ft-btn ft-btn--ghost"
                  type="button"
                  onClick={() => setShowNew(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="ft-btn ft-btn--primary"
                  type="button"
                  onClick={createBU}
                  disabled={saving}
                >
                  {saving ? "Creating…" : "Create"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {showAddEmployment ? (
          <div className="ft-modalOverlay" role="dialog" aria-modal="true">
            <div className="ft-modal">
              <div className="ft-modalHeader">
                <div>
                  <div className="ft-cardTitle">Add Employment</div>
                  <div className="ft-caption">
                    {selected
                      ? `${selected.groupName || "—"} / ${selected.storeName || "—"}${
                          selected.storeCode ? ` (${selected.storeCode})` : ""
                        }`
                      : "Selected Business Unit"}
                  </div>
                </div>

                <button
                  className="ft-btn ft-btn--ghost"
                  type="button"
                  onClick={() =>
                    !employmentSaving ? setShowAddEmployment(false) : null
                  }
                >
                  Close
                </button>
              </div>

              <div className="ft-modalBody">
                <div>
                  <div className="ft-label">User</div>
                  <select
                    className="ft-select"
                    value={employmentAppUserId}
                    onChange={(e) => setEmploymentAppUserId(e.target.value)}
                    disabled={employmentSaving || employmentCandidatesLoading}
                  >
                    <option value="">
                      {employmentCandidatesLoading
                        ? "Loading users..."
                        : employmentCandidates.length
                        ? "Select a user"
                        : "No available users"}
                    </option>
                    {employmentCandidates.map((u) => (
                      <option key={u.id} value={u.id}>
                        {`${u.name || "—"} — ${u.email || "—"}${
                          u.status ? ` (${u.status})` : ""
                        }`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="ft-label">Role</div>
                  <select
                    className="ft-select"
                    value={employmentRole}
                    onChange={(e) => setEmploymentRole(e.target.value)}
                    disabled={employmentSaving}
                  >
                    {employmentRoleOptionsForBusinessUnit(selected).map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <div className="ft-label">Employment Status</div>
                  <select
                    className="ft-select"
                    value={employmentIsActive ? "true" : "false"}
                    onChange={(e) =>
                      setEmploymentIsActive(e.target.value === "true")
                    }
                    disabled={employmentSaving}
                  >
                    <option value="true">ACTIVE</option>
                    <option value="false">INACTIVE</option>
                  </select>
                </div>
              </div>

              <div className="ft-modalFooter">
                <button
                  className="ft-btn ft-btn--ghost"
                  type="button"
                  onClick={() => setShowAddEmployment(false)}
                  disabled={employmentSaving}
                >
                  Cancel
                </button>
                <button
                  className="ft-btn ft-btn--primary"
                  type="button"
                  onClick={createEmploymentFromBusinessUnit}
                  disabled={employmentSaving || !employmentAppUserId}
                >
                  {employmentSaving ? "Adding…" : "Add Employment"}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
